import type {
  CompanyRequisitesTextInput,
  LegalEntity,
} from '@shared/types';
import { api } from './client';

export type CompanyRequisites = CompanyRequisitesTextInput & {
  id: string;
  legalEntity: LegalEntity;
  effectiveFrom: string;
  website?: string | null;
  phoneInternational?: string | null;
  hasVat: boolean;
  vatRate: number;
  vatExemption?: string | null;
  useBarcode: boolean;
  stampText: string;
  createdAt: string;
  updatedAt: string;
};

export type CompanyRequisitesAssetKind = 'LETTERHEAD_DOCX' | 'LOGO_SVG';

export interface CompanyRequisitesAsset {
  id: string;
  requisitesId: string;
  kind: CompanyRequisitesAssetKind;
  label: string | null;
  originalName: string;
  mimeType: string;
  extension: 'docx' | 'svg';
  sizeBytes: number;
  sha256: string;
  uploadedBy: {
    id: string;
    name: string | null;
  } | null;
  createdAt: string;
  updatedAt: string;
}

export interface CompanyRequisitesAssetsResponse {
  requisitesId: string;
  letterhead: CompanyRequisitesAsset | null;
  logos: CompanyRequisitesAsset[];
}

async function rethrowBlobApiError(error: unknown): Promise<never> {
  const response = (error as { response?: { data?: unknown } }).response;
  if (response?.data instanceof Blob) {
    const text = await response.data.text();
    try {
      response.data = JSON.parse(text) as unknown;
    } catch {
      response.data = { message: text || 'Не удалось получить файл' };
    }
  }
  throw error;
}

export const companyRequisitesApi = {
  getAll: async (): Promise<CompanyRequisites[]> => {
    const { data } = await api.get<CompanyRequisites[]>('/company-requisites');
    return data;
  },

  getAssets: async (id: string): Promise<CompanyRequisitesAssetsResponse> => {
    const { data } = await api.get<CompanyRequisitesAssetsResponse>(
      `/company-requisites/${id}/assets`,
    );
    return data;
  },

  downloadLetterhead: async (id: string): Promise<Blob> => {
    try {
      const { data } = await api.get<Blob>(
        `/company-requisites/${id}/assets/letterhead/download`,
        { responseType: 'blob' },
      );
      return data;
    } catch (error) {
      return rethrowBlobApiError(error);
    }
  },

  downloadLogo: async (id: string, assetId: string): Promise<Blob> => {
    try {
      const { data } = await api.get<Blob>(
        `/company-requisites/${id}/assets/logos/${assetId}/download`,
        { responseType: 'blob' },
      );
      return data;
    } catch (error) {
      return rethrowBlobApiError(error);
    }
  },
};
